import type { SojournDetailDto } from '@room-mates/shared';
import { type FormEvent, type ReactElement, useState } from 'react';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Button, Dialog, ErrorAlert } from '../../shared/ui/index.js';
import { DateRangeCalendar, type PartialDateRange, UNBOUNDED_RANGE } from './DateRangeCalendar.js';
import { useUpdateSojourn } from './sojournHooks.js';

export interface EditSojournDatesDialogProps {
  readonly sojourn: SojournDetailDto;
  readonly onClose: () => void;
}

/**
 * Moves a sojourn's window without touching its name or anything inside it.
 *
 * Unlike the guest and house date editors, which pick a range *within* the
 * sojourn's bounds, this dialog is what defines those bounds in the first
 * place, so the calendar here is given no real bounds of its own.
 *
 * Shrinking the window under a house's availability, a guest's stay or a
 * placement lock is refused by the server rather than silently stranding data
 * outside the new dates, so nothing about that rule is duplicated here.
 */
export function EditSojournDatesDialog({
  sojourn,
  onClose,
}: EditSojournDatesDialogProps): ReactElement {
  const t = useTranslation();
  const updateSojourn = useUpdateSojourn(sojourn.id);
  const [value, setValue] = useState<PartialDateRange>({
    start: sojourn.startDate,
    end: sojourn.endDate,
  });

  const unchanged = value.start === sojourn.startDate && value.end === sojourn.endDate;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (value.start === null || value.end === null) {
      return;
    }
    // Only the dates are ever sent — a rename belongs to its own flow, not to
    // whichever field happened to be open when this was submitted.
    updateSojourn.mutate({ startDate: value.start, endDate: value.end }, { onSuccess: onClose });
  };

  return (
    <Dialog title={t.editSojournDates.title} onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <ErrorAlert error={updateSojourn.error} />

        <DateRangeCalendar bounds={UNBOUNDED_RANGE} value={value} onChange={setValue} />

        <div className="row row--end">
          <Button variant="secondary" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button
            type="submit"
            loading={updateSojourn.isPending}
            disabled={unchanged || value.end === null}
          >
            {t.editSojournDates.saveDates}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
